import {useEffect, useRef} from 'react';
import {format, parse, parseISO} from 'date-fns';
import {useFormikContext} from 'formik';
import useTranslation from 'next-translate/useTranslation';

import {type OrderAccount} from '@/components/global/layout/EventBar/useOrderAccounts';
import {useFulfillmentDetailState} from '@/components/global/providers/FulfillmentDetailStateProvider';
import Modal from '@/components/ui/Modal';
import {FulfillmentDetailStrategy} from '@/graphql/types';
import useTracking from '@/hooks/useTracking';

type ApplyDetailsConfirmationModalProps = {
  isOpen: boolean;
  onReset: () => void;
};

type ConfirmationModalFormValues = {
  eventOrderType: FulfillmentDetailStrategy;
  addressString: string;
  eventDate: string | null;
  eventTime: string | null;
  attendees: number | null;
  eventName: string | null;
  orderAccount?: OrderAccount | null;
};

const ApplyDetailsConfirmationModal: React.FC<ApplyDetailsConfirmationModalProps> = ({
  isOpen,
  onReset,
}) => {
  const {t: tAppBar} = useTranslation('app-bar');
  const t = (key: string) => tAppBar(`eventBar.applyDetailsConfirmation.${key}`);
  const {track, trackClick} = useTracking();
  const {fulfillmentDetailClickHandler, FulfillmentDetailStates} = useFulfillmentDetailState();
  const {initialValues, submitForm, values, isSubmitting} =
    useFormikContext<ConfirmationModalFormValues>();
  const {addressString, attendees, eventName, eventTime, eventDate, eventOrderType, orderAccount} =
    values;

  // Track whether the modal is closing via an action button (Discard or Apply) rather than the ✕ /
  // outside press, so onOpenChange skips the close/reset path (prevents double tracking + a reopen loop).
  const isClosingViaCancelRef = useRef(false);

  const formatEventDateTime = (eventDate?: string | null, eventTime?: string | null): string => {
    if (!eventDate && !eventTime) return t('anytime');
    const date = eventDate ? format(parseISO(eventDate), 'EEEE, LLLL dd') : '';
    if (!eventTime) return date;
    const time = format(parse(eventTime, 'HH:mm:ss', new Date()), 'h:mm a');
    if (!eventDate) return time;
    return `${date}, ${time}`;
  };

  const formattedEventDate = formatEventDateTime(eventDate, eventTime);

  const getDirtyValues = () => {
    const data: Partial<Record<keyof ConfirmationModalFormValues, string | number | null>> = {};
    const keyValues = Object.keys(values) as (keyof ConfirmationModalFormValues)[];

    keyValues.forEach(key => {
      if (key === 'orderAccount') {
        if (values[key]?.id !== initialValues[key]?.id) {
          data[key] = values[key]?.id;
        }
      } else if (values[key] !== initialValues[key]) {
        data[key] = values[key];
      }
    });

    return data;
  };
  const pendingValues = getDirtyValues();
  const orderAccountPending = Object.keys(pendingValues).includes('orderAccount');
  const strategyPending = Object.keys(pendingValues).includes('eventOrderType');
  // values check is for edge case where someone clears out address input and clicks outside of dropdown
  const addressPending =
    Object.keys(pendingValues).includes('addressString') && pendingValues['addressString'] !== '';
  const dateTimePending =
    Object.keys(pendingValues).includes('eventDate') ||
    Object.keys(pendingValues).includes('eventTime');
  const headcountPending = Object.keys(pendingValues).includes('attendees');
  const eventNamePending = Object.keys(pendingValues).includes('eventName');

  const handleApplyClick = () => {
    // Apply closes the modal via the post-submit state change, not the ✕/outside press. Flag it (as
    // Discard does) so the modal's onOpenChange handler skips the close/reset path — otherwise that
    // path re-enters while the form is still dirty and reopens the modal in an infinite loop.
    isClosingViaCancelRef.current = true;
    trackClick('event-bar-apply-details-confirmation-modal-apply-button');
    // closing of the confirmation modal occurs after a submit
    submitForm();
  };

  const resetEventDetails = () => {
    fulfillmentDetailClickHandler(FulfillmentDetailStates.NONE);
    onReset();
  };

  const handleDiscardClick = () => {
    isClosingViaCancelRef.current = true;
    trackClick('event-bar-apply-details-confirmation-modal-discard-button');
    resetEventDetails();
  };

  const handleCloseClick = () => {
    // Only track and reset if NOT closing via cancel button
    if (!isClosingViaCancelRef.current) {
      trackClick('event-bar-apply-details-confirmation-modal-close-button');
      resetEventDetails();
    }
    // Reset flag after handling
    isClosingViaCancelRef.current = false;
  };

  useEffect(() => {
    if (!isOpen) return;

    track('event-bar-apply-details-confirmation-modal-open', {
      page: window.location.pathname,
      sub_category: 'navigation bar',
    });
  }, [isOpen, track]);

  return (
    <Modal
      cancelAction={{
        label: t('button.discard'),
        onClick: handleDiscardClick,
      }}
      // Clicking into the search bar (or any element outside the event bar) is what opens this
      // modal; without this, Base UI reads that same click as an outside press and dismisses the
      // modal instantly, silently discarding the pending changes. Force a Discard/Apply choice.
      disablePointerDismissal
      onOpenChange={open => !open && handleCloseClick()}
      open={isOpen}
      primaryAction={{
        closeOnComplete: false, // Closing is handled after submit
        label: t('button.apply'),
        loading: isSubmitting,
        onClick: handleApplyClick,
      }}
      title={t('title')}
    >
      <div
        className="grid grid-cols-[auto_1fr] gap-2"
        data-testid="apply-details-confirmation-modal"
      >
        {orderAccountPending && (
          <>
            <span className="font-bold">{t('orderAccount')}:</span>
            <span>{orderAccount?.label}</span>
          </>
        )}
        {strategyPending && (
          <>
            <span className="font-bold">{t('strategy')}:</span>
            <span>
              {eventOrderType === 'TAKEOUT'
                ? tAppBar('eventBar.eventLocation.orderType.Takeout')
                : tAppBar('eventBar.eventLocation.orderType.Delivery')}
            </span>
          </>
        )}
        {addressPending && (
          <>
            <span className="font-bold">{t('address')}:</span>
            <span>{addressString}</span>
          </>
        )}
        {dateTimePending && (
          <>
            <span className="font-bold">{t('dateTime')}:</span>
            <span>{formattedEventDate}</span>
          </>
        )}
        {headcountPending && (
          <>
            <span className="font-bold">{t('people')}:</span>
            <span>{Number.isFinite(attendees) ? attendees : t('none')}</span>
          </>
        )}
        {eventNamePending && (
          <>
            <span className="font-bold">{t('eventName')}:</span>
            <span>{eventName}</span>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ApplyDetailsConfirmationModal;
